import {
  LayoutDashboard,
  Users,
  IdCard,
  CalendarCheck,
  Heart,
  Wallet,
  MessageSquare,
  BarChart3,
  Settings,
  LogOut,
  ClipboardCheck,
  Ticket,
  Link2,
  ArrowLeftCircle,
  Gamepad2,
  Handshake,
  Trophy,
  Bot,
  ShieldCheck,
} from "lucide-react";
import logo from "@/gestionale/assets/logo.png";
import { NavLink } from "@/gestionale/components/NavLink";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/gestionale/hooks/useAuth";
import { useQuery } from "@tanstack/react-query";
import { getStagioneCorrente } from "@/gestionale/lib/stagione";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  useSidebar,
} from "@/gestionale/components/ui/sidebar";
import { Button } from "@/gestionale/components/ui/button";
import { Badge } from "@/gestionale/components/ui/badge";

const allMenuItems = [
  { title: "Dashboard", url: "/app", icon: LayoutDashboard, roles: ["admin", "segreteria"] },
  { title: "La mia Area", url: "/app", icon: LayoutDashboard, roles: ["atleta", "allenatore", "istruttore"] },
  { title: "Persone", url: "/app/persone", icon: Users, roles: ["admin", "segreteria"] },
  { title: "Certificati Medici", url: "/app/certificati-medici", icon: ShieldCheck, roles: ["admin", "segreteria"] },
  { title: "Soci", url: "/app/soci", icon: Heart, roles: ["admin", "segreteria"] },
  { title: "Abbonamenti", url: "/app/abbonamenti", icon: CalendarCheck, roles: ["admin", "segreteria"] },
  { title: "Tesseramenti", url: "/app/tesseramenti", icon: IdCard, roles: ["admin", "segreteria"] },
  { title: "Tessere Ingressi", url: "/app/tessere-ingressi", icon: Ticket, roles: ["admin", "segreteria"] },
  { title: "Presenze", url: "/app/presenze", icon: ClipboardCheck, roles: ["admin", "segreteria"] },
  { title: "Partite", url: "/app/partite", icon: Trophy, roles: ["admin"] },
  { title: "Comunicazioni", url: "/app/comunicazioni", icon: MessageSquare, roles: ["admin", "segreteria"] },
  { title: "Contabilità", url: "/app/contabilita", icon: Wallet, roles: ["admin", "segreteria"] },
  { title: "Report", url: "/app/report", icon: BarChart3, roles: ["admin", "segreteria"] },
  { title: "Sponsor", url: "/app/sponsor", icon: Handshake, roles: ["admin", "segreteria"] },
  { title: "Migrazione Movimenti", url: "/app/migrazione-movimenti", icon: Link2, roles: ["admin"] },
  { title: "IA Rubiana Calcio", url: "/app/ia-rubiana", icon: Bot, roles: ["admin"] },
  { title: "Impostazioni", url: "/app/impostazioni", icon: Settings, roles: ["admin"] },
  // Il minigioco è raggiungibile da chiunque abbia un account nel
  // gestionale, a prescindere dal ruolo.
  { title: "Gioco", url: "/app/gioco", icon: Gamepad2, roles: ["admin", "segreteria", "allenatore", "atleta", "istruttore"] },
];

const ROLE_LABELS: Record<string, string> = {
  admin: "Amministratore",
  segreteria: "Segreteria",
  allenatore: "Allenatore",
  atleta: "Atleta",
  istruttore: "Istruttore",
};

export function AppSidebar() {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";
  const location = useLocation();
  const { role, user, signOut } = useAuth();
  const { data: stagioneCorrente = "2026/2027" } = useQuery({
    queryKey: ["stagione-corrente"],
    queryFn: getStagioneCorrente,
  });

  const menuItems = allMenuItems.filter((item) => role && item.roles.includes(role));

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="p-4">
        <div className="flex items-center gap-3">
          <img src={logo} alt="Rubiana Calcio" className="h-9 w-9 shrink-0 rounded-lg object-contain" />
          {!collapsed && (
            <div className="flex flex-col">
              <span className="text-sm font-bold tracking-tight text-sidebar-foreground">
                Rubiana Calcio
              </span>
              <span className="text-[11px] text-sidebar-foreground/60">
                Gestionale ASD
              </span>
            </div>
          )}
        </div>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel className="text-sidebar-foreground/40 text-[11px] uppercase tracking-wider">
            Menu
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {menuItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink
                      to={item.url}
                      end={item.url === "/app"}
                      className="hover:bg-sidebar-accent/60 transition-colors"
                      activeClassName="bg-sidebar-accent text-sidebar-primary font-semibold"
                    >
                      <item.icon className="mr-2 h-4 w-4" />
                      {!collapsed && <span>{item.title}</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="p-4 space-y-3">
        {!collapsed && role && (
          <div className="flex items-center justify-center">
            <Badge variant="outline" className="text-[10px] border-sidebar-border text-sidebar-foreground/60">
              {ROLE_LABELS[role] || role}
            </Badge>
          </div>
        )}

        {/* Torna al sito pubblico (mantiene la sessione attiva) */}
        {!collapsed && (
          <Button
            asChild
            variant="ghost"
            size="sm"
            className="w-full text-yellow-400/80 hover:text-yellow-400 hover:bg-sidebar-accent/60"
          >
            <Link to="/">
              <ArrowLeftCircle className="mr-2 h-4 w-4" />
              Torna al sito
            </Link>
          </Button>
        )}
        {collapsed && (
          <Button
            asChild
            variant="ghost"
            size="icon"
            className="w-full text-yellow-400/80 hover:text-yellow-400"
          >
            <Link to="/">
              <ArrowLeftCircle className="h-4 w-4" />
            </Link>
          </Button>
        )}

        {!collapsed && (
          <Button
            variant="ghost"
            size="sm"
            className="w-full text-red-400/80 hover:text-red-400 hover:bg-sidebar-accent/60"
            onClick={signOut}
          >
            <LogOut className="mr-2 h-4 w-4" />
            Esci
          </Button>
        )}
        {collapsed && (
          <Button
            variant="ghost"
            size="icon"
            className="w-full text-red-400/80 hover:text-red-400"
            onClick={signOut}
          >
            <LogOut className="h-4 w-4" />
          </Button>
        )}
        {!collapsed && (
          <p className="text-[11px] text-sidebar-foreground/40 text-center">
            Stagione {stagioneCorrente}
          </p>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}